import type { OnChanges, OnInit } from '@angular/core';
import { Directive, HostBinding, Input } from '@angular/core';
import type { SafeUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import type { Banner } from '@core-mkt/shared/components/banner/banner/banner';
import { mockRoute } from '@core-mkt/shared/components/banner/banner/banner-mock-data';
import { cloneDeep } from 'lodash-es';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';
import { DateProxy } from '../countdown/date-proxy';
import { CouponQueryResolverService } from '../coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '../coupon-query-resolver/coupon-use-cases';
import { EnvService } from '../env/env.service';
import type { QueryParam } from './query-helpers';
import { getParsedLinkString, getQueryParamString } from './query-helpers';
import { QueryParamService } from './query-param.service';

@Directive({
  selector: 'a[href]',
  standalone: true,
})
export class QueryParamDirective implements OnInit, OnChanges {
  @HostBinding('attr.href') hrefAttr: string | SafeUrl = '';
  @Input() href: string;
  queryParams: QueryParam;
  resolverData: PageDataResponse;
  pageData: Banner = null;
  isOfferTime: boolean = null;

  globalSalesQueryParam: string;

  constructor(
    private route: ActivatedRoute,
    private brand: BrandConfigurationService,
    private sanitizer: DomSanitizer,
    private queryParamService: QueryParamService,
    private couponQueryResolverService: CouponQueryResolverService,
    private env: EnvService
  ) {
    // Set Query value
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);

    if (!this.route.snapshot.data) {
      this.resolverData = mockRoute.snapshot.data.pageData;
    } else {
      if (this.route.snapshot.data.pageData) {
        this.resolverData = cloneDeep(this.route.snapshot.data.pageData as PageDataResponse);
        this.pageData = this.resolverData.craftData.header?.aceHeaderPromoBanner
          ? this.resolverData.craftData.header.aceHeaderPromoBanner[0]
          : this.resolverData.craftData.header?.realEstateHeaderPromoBanner?.[0] ?? null;
      }
    }

    this.isOfferTime = this.isOfferTimeRange();
  }

  ngOnInit(): void {
    this.queryParams = this.queryParamService.getQueryParams();
    //console.log('Query Params:', this.queryParams);
    this.setHref();
  }

  ngOnChanges() {
    this.setHref();
  }

  setHref(): void {
    const hostName = this.brand.hostName;
    const siblingSites = this.brand.siblingSites;
    const blogPrefix = this.env.get.blogPrefix;

    const queryString = getQueryParamString(this.queryParams, this.href, blogPrefix);
    const parsedLink = getParsedLinkString(this.href, queryString, hostName, siblingSites, blogPrefix);
    this.hrefAttr = this.addOverWriteCoupon(parsedLink);
  }

  addOverWriteCoupon(parsedLink: string): string | SafeUrl {
    // Banner overwriteCoupon only applies when the banner is BOTH within its
    // active window AND visible to the user (showHidePromoBanner === true).
    // Without the showHidePromoBanner gate, a hidden banner would silently
    // inject its coupon as the sales param at checkout, defeating Craft's
    // visibility toggle. EPS-3934.
    if (!this.isOfferTime) return parsedLink;
    if (!this.pageData?.overwriteCoupon) return parsedLink;
    if (this.pageData.showHidePromoBanner !== true) return parsedLink;
    if (!(parsedLink.includes('checkout') || parsedLink.includes('productId'))) return parsedLink;
    if (parsedLink.includes('undefined')) return parsedLink;

    const url = new URL(parsedLink);
    const params = new URLSearchParams(url.search);
    params.set(this.globalSalesQueryParam, this.pageData.overwriteCoupon);
    //setting params to string replaces the comma with '%2C'. We need to replace it back to ','
    url.search = params.toString().replace(/%2C/g, ',');
    return url.toString();
  }

  private isOfferTimeRange = (): boolean => {
    let endTime = null;
    let startTime = null;
    const proxy = new DateProxy();

    if (this.pageData?.promotionEndsDateTime && this.pageData?.promotionStartDateTime) {
      startTime = new Date(this.pageData?.promotionStartDateTime).valueOf();
      const resetEndDate = this.pageData?.promotionReset[0]?.promotionEndsDateTime;
      endTime = resetEndDate
        ? new Date(resetEndDate).valueOf()
        : new Date(this.pageData?.promotionEndsDateTime).valueOf();
    } else {
      if (!this.pageData?.promotionEndsDateTime && !this.pageData?.promotionStartDateTime) {
        startTime = proxy.date.setHours(0, 0, 0, 0);
        endTime = proxy.date.setHours(24, 0, 0, 0);
      } else {
        return false;
      }
    }

    return startTime <= proxy.now && proxy.now <= endTime;
  };
}
